import { FaCrown } from "react-icons/fa6";
import { BsFillTrophyFill } from "react-icons/bs";
import { BsLightningChargeFill } from "react-icons/bs";

export const STRIPE_IDS = {
  // CRAYO_HOBBY_MONTHLY: "price_1OasD4IT0LdPat3S1oZcegkk",
  CRAYO_HOBBY_MONTHLY: "price_1OgXrRIT0LdPat3Sq9947siC",
  CRAYO_HOBBY_YEARLY: "price_1OgXv2IT0LdPat3SfqRIf7K9",
  CRAYO_CLIPPER_MONTHLY: "price_1OgXtYIT0LdPat3SYLLTsJhb",
  CRAYO_CLIPPER_YEARLY: "price_1OgXuSIT0LdPat3SXAqpyvnN",
  CRAYO_PRO_MONTHLY: "price_1OgXvuIT0LdPat3S8DTb0yJo",
  CRAYO_PRO_YEARLY: "price_1OgXwDIT0LdPat3SPFUVjOhL",
  CRAYO_BFCM_PACKAGE: "price_1QPcfMIT0LdPat3SibaXe0V2",
  // CRAYO_BFCM_PACKAGE: "price_1QPe2VIT0LdPat3S64I1uhlq",
};

export const STRIPE_PRODUCT_NAMES: Record<string, string> = {
  [STRIPE_IDS.CRAYO_HOBBY_MONTHLY]: "Hobby",
  [STRIPE_IDS.CRAYO_HOBBY_YEARLY]: "Hobby",
  [STRIPE_IDS.CRAYO_CLIPPER_MONTHLY]: "Clipper",
  [STRIPE_IDS.CRAYO_CLIPPER_YEARLY]: "Clipper",
  [STRIPE_IDS.CRAYO_PRO_MONTHLY]: "Pro",
  [STRIPE_IDS.CRAYO_PRO_YEARLY]: "Pro",
};

type Promos = "bfcm";

export type PurchasePlan = {
  name: string;
  monthly: { priceId: string; price: number };
  yearly: { priceId: string; price: number };
  promo: {
    [key in Promos]: {
      monthly: { priceId: string; price: number };
      yearly: { priceId: string; price: number };
    };
  };
  icon: React.ComponentType<any>;
  values: {
    videos: number;
    exports: number;
    voiceovers: number;
    images: number;
  };
  features: string[];
};

export const PURCHASE_PLANS = [
  {
    name: "Hobby",
    monthly: { priceId: STRIPE_IDS.CRAYO_HOBBY_MONTHLY, price: 19 },
    yearly: { priceId: STRIPE_IDS.CRAYO_HOBBY_YEARLY, price: 160 },
    promo: {
      bfcm: {
        monthly: { priceId: STRIPE_IDS.CRAYO_HOBBY_MONTHLY, price: 3 },
      },
    },
    values: {
      videos: 50,
      exports: 40 * 60,
      voiceovers: 30 * 60,
      images: 100,
    },
    icon: BsLightningChargeFill,
    features: ["40 minutes of export", "30 voiceover minutes", "100 AI images"],
  },
  {
    name: "Clipper",
    monthly: { priceId: STRIPE_IDS.CRAYO_CLIPPER_MONTHLY, price: 39 },
    yearly: { priceId: STRIPE_IDS.CRAYO_CLIPPER_YEARLY, price: 327 },
    promo: {
      bfcm: {
        monthly: { priceId: STRIPE_IDS.CRAYO_CLIPPER_MONTHLY, price: 5 },
      },
    },
    values: {
      videos: 150,
      exports: 120 * 60,
      voiceovers: 120 * 60,
      images: 300,
    },
    icon: FaCrown,
    features: ["2 hours of export", "120 voiceover minutes", "300 AI images"],
  },
  {
    name: "Pro",
    monthly: { priceId: STRIPE_IDS.CRAYO_PRO_MONTHLY, price: 79 },
    yearly: { priceId: STRIPE_IDS.CRAYO_PRO_YEARLY, price: 664 },
    promo: {
      bfcm: {
        // TODO: update stripe ids + yearly price
        monthly: { priceId: STRIPE_IDS.CRAYO_PRO_MONTHLY, price: 10 },
        yearly: { priceId: STRIPE_IDS.CRAYO_PRO_YEARLY, price: 664 },
      },
    },
    values: {
      videos: 250,
      exports: 240 * 60,
      voiceovers: 180 * 60,
      images: 500,
    },
    icon: BsFillTrophyFill,
    features: ["3 hours of export", "180 voiceover minutes", "500 AI images"],
  },
] as PurchasePlan[];

export const PRICE_ID_TO_VALUES: Record<string, PurchasePlan> = {};

PURCHASE_PLANS.forEach((plan) => {
  PRICE_ID_TO_VALUES[plan.monthly.priceId] = plan;
  PRICE_ID_TO_VALUES[plan.yearly.priceId] = plan;
});

type PlanInterval = "monthly" | "yearly" | "unknown";
export function getPlanIntervalByPriceId(priceId: string): PlanInterval {
  for (const plan of PURCHASE_PLANS) {
    if (plan.monthly.priceId === priceId) return "monthly";
    if (plan.yearly.priceId === priceId) return "yearly";
  }
  return "unknown";
}

export const getPlanNameByPriceId = (priceId: string) => {
  return STRIPE_PRODUCT_NAMES[priceId];
};

export const FEATURES = [
  { type: "Voiceover minutes", hobby: 30, clipper: 120, pro: 180 },
  { type: "Export minutes", hobby: 40, clipper: 120, pro: 240 },
  { type: "AI video credits", hobby: 40, clipper: 150, pro: 250 },
  { type: "AI image credits", hobby: 100, clipper: 300, pro: 500 },
  { type: "AI script credits", hobby: 100, clipper: 300, pro: 500 },
];

export const FAQ_DATA = [
  {
    question: "Can I cancel my plan?",
    answer:
      "Yes. You can cancel your plan anytime via settings. Your plan will remain active until the end of the billing cycle.",
  },
  {
    question: "How do I view how many credits I have left?",
    answer:
      "You can view your usage in settings by clicking your profile picture in the top right corner.",
  },
  {
    question: "Can I change my plan after I subscribe?",
    answer: "Yes. You can upgrade or downgrade your plan at any time by visiting account settings.",
  },
  {
    question: "Do you have a refund policy?",
    answer:
      "Unfortunately, we do not offer refunds. You can cancel your plan anytime and your plan will remain active until the end of the billing cycle.",
  },
  {
    question: "What is an export minute?",
    answer:
      "An export minute is the length of video you can generate with Crayo. For example, if you have 60 export minutes, you can generate a 1 minute video 60 times.",
  },
  {
    question: "What is an AI video credit?",
    answer:
      "An AI video is a video auto-edited by Crayo's AI. We currently generate Story and ChatGPT videos.",
  },
  {
    question: "Can I monetize videos created with Crayo?",
    answer:
      "Yes. You fully own the rights to all videos. We use custom recorded gameplay to ensure originality.",
  },
  {
    question: "Can I generate in other languages?",
    answer:
      "Yes. We support all languages. Simply provide the text in the language you want to generate in.",
  },
  {
    question: "Can I import image from ChatGPT to Crayo?",
    answer: "Yes. You can import images to both our editor, and AI workflows.",
  },
] as const;

export const TOPUP_PLANS = [
  {
    price: 5,
    description:
      "Just a few extra credits to finish up your current project before your next billing cycle.",
    values: {
      export: 10 * 60,
      voiceover: 10 * 60,
      video: 10,
      image: 50,
    },
  },
  {
    price: 13,
    description: "A good amount of credits before being sure if you want to upgrade.",
    values: {
      export: 40 * 60,
      voiceover: 40 * 60,
      video: 45,
      image: 125,
    },
  },
  {
    price: 28,
    description: "Enough credits to scale a channel up without worrying about running out.",
    values: {
      export: 120 * 60,
      voiceover: 90 * 60,
      video: 125,
      image: 300,
    },
  },
];
